import * as React from "react";
import {
  Input,
  Textarea,
  Dropdown,
  Option,
  makeStyles,
  shorthands,
  useId,
} from "@fluentui/react-components";
import FormHeader from "./FormHeader";
import { useLocation } from "react-router";
import type { Schaden } from "./DamagesTable";

const useStyles = makeStyles({
  root: {
    ...shorthands.padding("20px"),
    display: "flex",
    flexDirection: "column",
    gap: "32px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px 80px",
    alignItems: "start",
  },
  divider: {
    gridColumn: "1 / span 2",
    borderBottom: "2px solid #ddd",
    margin: "16px 0",
  },
  fieldRow: {
    display: "grid",
    gridTemplateColumns: "45% 55%",
    alignItems: "center",
    gap: "8px",
    minHeight: "40px",
  },
  label: {
    textAlign: "left",
    paddingRight: "8px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontWeight: 400,
    fontSize: "16px",
    letterSpacing: "1px",
    position: "relative",
    "&::after": {
      content:
        "'........................................................................'",
      left: "100%",
      top: "0%",
      color: "#ccc",
      fontSize: "16px",
      pointerEvents: "none",
      whiteSpace: "nowrap",
      width: "100%",
      overflow: "hidden",
    },
  },
  inputContainer: {
    width: "100%",
  },
  inputFullWidth: {
    width: "100%",
  },
  descriptionContainer: {
    gridColumn: "1 / span 2",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  textareaFullWidth: {
    width: "100%",
    minHeight: "120px",
  },
  fileContainer: {
    gridColumn: "1 / span 2",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
});

export interface SchadenFormValues {
  schaden?: string;
  kontakt?: string;
  vertrag?: string;
  schadentag?: string;
  schadenort?: string;
  beschreibung?: string;
  schadenhoehe?: string;
  sb?: string;
  zahlung?: string;
  erstellt?: string;
  anspruchsteller?: string;
  vorgangsNr?: string;
  sachbearbeiter?: string;
  status?: string;
  files?: File[];
}

interface SchadenFormProps {
  initialValues?: Schaden;
}

const schadenOptions = [
  "Leitungswasserschaden",
  "Brand",
  "Einbruch",
  "Sturmschaden",
  // ... weitere Schadensarten
];
const kontaktOptions = ["EURENT GmbH", "Muster AG", "Beispiel GmbH"]; // Beispiel
const vertragOptions = ["V12345", "V67890"]; // Beispiel
const vorgangOptions = ["P-001", "P-002", "P-003"]; // Beispiel
const sachbearbeiterOptions = ["Max Müller", "Erika Mustermann"]; // Beispiel
const statusOptions = ["Offen", "In Bearbeitung", "Abgeschlossen"];

export const DamagesForm: React.FC<SchadenFormProps> = ({
  initialValues = {} as Schaden,
}) => {
  const styles = useStyles();
  const idSchaden = useId();
  const idKontakt = useId();
  const idVertrag = useId();
  const idVorgang = useId();
  const idSachb = useId();
  const idStatus = useId();

  const today = new Date().toISOString().split("T")[0];

  const location = useLocation();
  initialValues = location.state?.damage || ({} as Schaden);

  const [schaden, setSchaden] = React.useState<string[]>(
    initialValues.schaden ? [initialValues.schaden] : [],
  );
  const [kontakt, setKontakt] = React.useState<string[]>(
    initialValues.KontaktName ? [initialValues.KontaktName] : [],
  );
  const [vertrag, setVertrag] = React.useState<string[]>(
    initialValues.Vertragsnummer ? [initialValues.Vertragsnummer] : [],
  );
  const [schadentag, setSchadentag] = React.useState(
    initialValues.SchadenDatum ?? today,
  );
  const [schadenort, setSchadenort] = React.useState(
    initialValues.RisikoOrt ?? "",
  );
  const [schadenhoehe, setSchadenhoehe] = React.useState(
    initialValues.schadenhoehe ?? "",
  );
  const [zahlung, setZahlung] = React.useState("");
  const [erstellt, setErstellt] = React.useState(today);
  const [anspruchsteller, setAnspruchsteller] = React.useState(
    initialValues.anspruchsteller ?? "",
  );
  const [vorgangsNr, setVorgangsNr] = React.useState<string[]>(
    initialValues.vorgangsNr ? [initialValues.vorgangsNr] : [],
  );
  const [sachbearbeiter, setSachbearbeiter] = React.useState<string[]>(
    initialValues.Berater ? [initialValues.Berater] : [],
  );
  const [status, setStatus] = React.useState<string[]>(
    initialValues.Status ? [initialValues.Status] : [],
  );
  const [files, setFiles] = React.useState<FileList | null>(null);

  return (
    <div className={styles.root}>
      <FormHeader
        title={
          schadenort.length > 0
            ? "Schaden: " + kontakt + ", " + schadenort
            : "Neuer Schaden"
        }
      />
      <div className={styles.grid}>
        {/* Schaden, Kontakt, Vertrag */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Schaden:</span>
          <Dropdown
            clearable
            className={styles.inputFullWidth}
            selectedOptions={schaden}
            defaultValue={schadenOptions[0]}
            onOptionSelect={(_, data) =>
              setSchaden(data.optionValue ? [data.optionValue] : [])
            }
            placeholder="Schadensart wählen"
            id={idSchaden}
          >
            {schadenOptions.map((s) => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Dropdown>
        </div>
        <div className={styles.fieldRow}>
          <span className={styles.label}>Kontakt:</span>
          <Dropdown
            clearable
            className={styles.inputFullWidth}
            defaultValue={kontakt[0]}
            selectedOptions={kontakt}
            onOptionSelect={(_, data) =>
              setKontakt(data.optionValue ? [data.optionValue] : [])
            }
            placeholder="Kontakt wählen"
            id={idKontakt}
          >
            {kontaktOptions.map((k) => (
              <Option key={k} value={k}>
                {k}
              </Option>
            ))}
          </Dropdown>
        </div>
        <div className={styles.fieldRow}>
          <span className={styles.label}>Vertrag:</span>
          <Dropdown
            clearable
            className={styles.inputFullWidth}
            defaultValue={vertrag[0]}
            selectedOptions={vertrag}
            onOptionSelect={(_, data) =>
              setVertrag(data.optionValue ? [data.optionValue] : [])
            }
            placeholder="Vertrag wählen"
            id={idVertrag}
          >
            {vertragOptions.map((v) => (
              <Option key={v} value={v}>
                {v}
              </Option>
            ))}
          </Dropdown>
        </div>

        {/* Divider */}
        <div className={styles.divider} />

        {/* Datum und Ort */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Schadentag:</span>
          <Input
            type="date"
            className={styles.inputFullWidth}
            value={schadentag}
            onChange={(_, d) => setSchadentag(d.value)}
          />
        </div>
        <div className={styles.fieldRow}>
          <span className={styles.label}>Schadenort:</span>
          <Input
            className={styles.inputFullWidth}
            value={schadenort}
            placeholder="Ort"
            onChange={(_, d) => setSchadenort(d.value)}
          />
        </div>

        {/* Divider */}
        <div className={styles.divider} />

        {/* Finanzielle Angaben */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Schadenhöhe:</span>
          <Input
            className={styles.inputFullWidth}
            value={schadenhoehe}
            placeholder="Betrag"
            onChange={(_, d) => setSchadenhoehe(d.value)}
          />
        </div>
        <div className={styles.fieldRow}>
          <span className={styles.label}>Zahlung:</span>
          <Input
            className={styles.inputFullWidth}
            value={zahlung}
            placeholder="Zahlung"
            onChange={(_, d) => setZahlung(d.value)}
          />
        </div>

        {/* Erstellt, Anspruchsteller */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Erstellt:</span>
          <Input
            type="date"
            className={styles.inputFullWidth}
            value={erstellt}
            onChange={(_, d) => setErstellt(d.value)}
          />
        </div>
        <div className={styles.fieldRow}>
          <span className={styles.label}>Anspruchsteller:</span>
          <Input
            className={styles.inputFullWidth}
            value={anspruchsteller}
            placeholder="Name"
            onChange={(_, d) => setAnspruchsteller(d.value)}
          />
        </div>

        {/* Vorgang, Sachbearbeiter, Status */}
        <div className={styles.fieldRow}>
          <span className={styles.label}>Vorgangs-Nr.:</span>
          <Dropdown
            clearable
            className={styles.inputFullWidth}
            selectedOptions={vorgangsNr}
            onOptionSelect={(_, data) =>
              setVorgangsNr(data.optionValue ? [data.optionValue] : [])
            }
            placeholder="Vorgangsnummer wählen"
            id={idVorgang}
          >
            {vorgangOptions.map((p) => (
              <Option key={p} value={p}>
                {p}
              </Option>
            ))}
          </Dropdown>
        </div>
        <div className={styles.fieldRow}>
          <span className={styles.label}>Sachbearbeiter:</span>
          <Dropdown
            clearable
            className={styles.inputFullWidth}
            defaultValue={sachbearbeiter[0]}
            selectedOptions={sachbearbeiter}
            onOptionSelect={(_, data) =>
              setSachbearbeiter(data.optionValue ? [data.optionValue] : [])
            }
            placeholder="Sachbearbeiter wählen"
            id={idSachb}
          >
            {sachbearbeiterOptions.map((s) => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Dropdown>
        </div>
        <div className={styles.fieldRow}>
          <span className={styles.label}>Status:</span>
          <Dropdown
            clearable
            className={styles.inputFullWidth}
            defaultValue={status[0]}
            selectedOptions={status}
            onOptionSelect={(_, data) =>
              setStatus(data.optionValue ? [data.optionValue] : [])
            }
            placeholder="Status wählen"
            id={idStatus}
          >
            {statusOptions.map((s) => (
              <Option key={s} value={s}>
                {s}
              </Option>
            ))}
          </Dropdown>
        </div>

        {/* {/1* Dateien *1/} */}
        {/* <div className={styles.fileContainer}> */}
        {/*   <span className={styles.label}>Dateien hochladen:</span> */}
        {/*   <Input */}
        {/*     type="file" */}
        {/*     multiple */}
        {/*     className={styles.inputFullWidth} */}
        {/*     onChange={(e) => setFiles(e.target.files)} */}
        {/*   /> */}
        {/* </div> */}
      </div>
    </div>
  );
};
